import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { updateMarkup } from 'features/kestrelViewer/utils/updateMarkup'
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Vector2 } from 'three'
import {
  GeckoKestrelModel,
  KVAnnotation,
  KVCameraPose,
  KVCameraView,
  KVMarkup,
  KVMeasurement,
  KVPlaneIntersection,
  KVPolygon,
  KVSurfaceArea,
} from 'types'
import { useMemoizedObject } from 'utils/hookHelpers'

/**
 * An object containing various states for the Kestrel viewer stream.
 */
export interface KestrelStatus {
  /** Whether the photogrammetry toggle is pressed. */
  toggledOn?: boolean
  /** Whether the connection should remain open (persists for some time after toggle is off). */
  shouldConnect?: boolean
  /** Whether the connection has been established and video is ready to be displayed. */
  videoReady?: boolean
}

export type KestrelContextType = {
  /** The Kestrel Model Entry for the current component. */
  kestrelModel: GeckoKestrelModel | undefined

  /** Function to set the Kestrel Model Entry for the current component. */
  setKestrelModel: React.Dispatch<
    React.SetStateAction<GeckoKestrelModel | undefined>
  >

  /** Authorized axios instance for API calls. */
  authorizedAxios: AuthedAxiosRequests

  /** A collection of values to monitor the Kestrel viewer stream configuration and connection. */
  kestrelStatus: KestrelStatus

  /** Set the collection of values to monitor the Kestrel viewer stream configuration and connection. */
  setKestrelStatus: React.Dispatch<React.SetStateAction<KestrelStatus>>

  /** Currently selected Kestrel Viewer markup. */
  selectedMarkup: KVMarkup | undefined

  /** Function to set the currently selected Kestrel Viewer markup. */
  setSelectedMarkup: Dispatch<SetStateAction<KVMarkup | undefined>>

  /** Function to send a json message to the Kestrel Viewer stream. */
  sendKVCommand: ((json: object | string) => void) | undefined

  /** Function to set the function to send a json message to the Kestrel Viewer stream. */
  setSendKVCommand: Dispatch<
    SetStateAction<((json: object | string) => void) | undefined>
  >

  /** The starting position of the popup menu. */
  popupMenuStartPosition: Vector2

  /** Function to set the starting position of the popup menu. */
  setPopupMenuStartPosition: Dispatch<SetStateAction<Vector2>> | undefined

  /** The current pixel streaming URL for Kestrel viewer. */
  pixelStreamingURL: string

  /** Function to set the pixel streaming URL. */
  setPixelStreamingURL: React.Dispatch<React.SetStateAction<string>>

  // Model data arrays
  annotations: KVAnnotation[] | undefined
  setAnnotations: Dispatch<SetStateAction<KVAnnotation[] | undefined>>

  polygons: KVPolygon[] | undefined
  setPolygons: Dispatch<SetStateAction<KVPolygon[] | undefined>>

  measurements: KVMeasurement[] | undefined
  setMeasurements: Dispatch<SetStateAction<KVMeasurement[] | undefined>>

  planeIntersections: KVPlaneIntersection[] | undefined
  setPlaneIntersections: Dispatch<
    SetStateAction<KVPlaneIntersection[] | undefined>
  >

  surfaceAreas: KVSurfaceArea[] | undefined
  setSurfaceAreas: Dispatch<SetStateAction<KVSurfaceArea[] | undefined>>
  cameraViews: KVCameraView[] | undefined
  setCameraViews: Dispatch<SetStateAction<KVCameraView[] | undefined>>
  cameraPoses: KVCameraPose[] | undefined
  setCameraPoses: Dispatch<SetStateAction<KVCameraPose[] | undefined>>

  // Model update functions
  updateAnnotation: (
    annotation: KVAnnotation,
    jsonField: string | string[],
    setter: (a: KVAnnotation) => KVAnnotation,
    v: unknown | unknown[]
  ) => void

  updatePolygon: (
    annotation: KVPolygon,
    jsonField: string | string[],
    setter: (a: KVPolygon) => KVPolygon,
    v: unknown | unknown[]
  ) => void

  updateMeasurement: (
    measurement: KVMeasurement,
    jsonField: string | string[],
    setter: (a: KVMeasurement) => KVMeasurement,
    v: string | boolean | (string | boolean)[]
  ) => void

  updatePlaneIntersection: (
    planeIntersection: KVPlaneIntersection,
    jsonField: string | string[],
    setter: (a: KVPlaneIntersection) => KVPlaneIntersection,
    v: unknown | unknown[]
  ) => void

  updateSurfaceArea: (
    surfaceArea: KVSurfaceArea,
    jsonField: string | string[],
    setter: (a: KVSurfaceArea) => KVSurfaceArea,
    v: unknown | unknown[]
  ) => void
}

const KestrelContext = React.createContext<KestrelContextType | undefined>(
  undefined
)

export const KestrelContextProvider: React.FC<
  KestrelContextType & {
    children: React.ReactNode
  }
> = ({ children, ...props }) => {
  const context = useMemoizedObject(props)
  return (
    <KestrelContext.Provider value={context}>
      {children}
    </KestrelContext.Provider>
  )
}

export const KestrelProvider: React.FC<{
  kestrelModels: ReadonlyArray<GeckoKestrelModel>
  children: React.ReactNode
}> = ({ kestrelModels, children }) => {
  const { authorizedAxios } = useGeckoAuth()

  // Basic Kestrel state
  const [kestrelModel, setKestrelModel] = useState<
    GeckoKestrelModel | undefined
  >(undefined)

  React.useEffect(() => {
    if (kestrelModels.length > 0) {
      // Retrieve the first kestrel model entry. We might want to change this in the future.
      setKestrelModel(kestrelModels.at(0))
    }
  }, [kestrelModels, setKestrelModel])

  const [kestrelStatus, setKestrelStatus] = useState<KestrelStatus>({
    toggledOn: false,
    shouldConnect: false,
    videoReady: false,
  })

  // auto start Kestrel if we have models, using useEffect in case it takes time for the model avail. to load
  React.useEffect(() => {
    if (kestrelModels.length > 0 && !kestrelStatus.toggledOn) {
      setKestrelStatus({
        toggledOn: true,
        shouldConnect: true,
        videoReady: false,
      })
    }
  }, [kestrelModels.length, kestrelStatus.toggledOn])

  const [selectedMarkup, setSelectedMarkup] = useState<KVMarkup | undefined>(
    undefined
  )

  const [sendKVCommand, setSendKVCommand] = useState<
    ((json: object | string) => void) | undefined
  >(undefined)

  const [popupMenuStartPosition, setPopupMenuStartPosition] = useState<Vector2>(
    new Vector2(0, 0)
  )

  const [pixelStreamingURL, setPixelStreamingURL] = useState<string>('')

  // Model data arrays
  const [annotations, setAnnotations] = useState<KVAnnotation[] | undefined>(
    undefined
  )

  const [polygons, setPolygons] = useState<KVPolygon[] | undefined>(undefined)

  const [measurements, setMeasurements] = useState<KVMeasurement[] | undefined>(
    undefined
  )

  const [planeIntersections, setPlaneIntersections] = useState<
    KVPlaneIntersection[] | undefined
  >(undefined)

  const [surfaceAreas, setSurfaceAreas] = useState<KVSurfaceArea[] | undefined>(
    undefined
  )

  const [cameraViews, setCameraViews] = useState<KVCameraView[] | undefined>(
    undefined
  )

  const [cameraPoses, setCameraPoses] = useState<KVCameraPose[] | undefined>(
    undefined
  )

  // Dependencies for update functions
  const { rolodexPatch } = useRolodexEdit()

  // Update functions
  const updateAnnotation = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType: 'updateAnnotation',
          markupArray: annotations,
          setMarkupArray: setAnnotations,
          rolodexField: 'annotations',
          toJSON: KVAnnotation.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      annotations,
      setAnnotations,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updatePolygon = useMemo(
    () =>
      (
        polygon: KVPolygon,
        jsonFields: string | string[],
        setter: (a: KVPolygon) => KVPolygon,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: polygon,
          jsonFields,
          setter,
          v,
          commandType: 'updatePolygon',
          markupArray: polygons,
          setMarkupArray: setPolygons,
          rolodexField: 'polygons',
          toJSON: KVPolygon.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      polygons,
      setPolygons,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updateMeasurement = useMemo(
    () =>
      (
        measurement: KVMeasurement,
        jsonFields: string | string[],
        setter: (a: KVMeasurement) => KVMeasurement,
        v: string | boolean | (string | boolean)[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: measurement,
          jsonFields,
          setter,
          v,
          commandType: 'updateMeasurement',
          markupArray: measurements,
          setMarkupArray: setMeasurements,
          rolodexField: 'measurements',
          toJSON: KVMeasurement.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      measurements,
      setMeasurements,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updatePlaneIntersection = useMemo(
    () =>
      (
        planeIntersection: KVPlaneIntersection,
        jsonFields: string | string[],
        setter: (a: KVPlaneIntersection) => KVPlaneIntersection,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: planeIntersection,
          jsonFields,
          setter,
          v,
          commandType: 'updatePlaneIntersection',
          markupArray: planeIntersections,
          setMarkupArray: setPlaneIntersections,
          rolodexField: 'planeIntersections',
          toJSON: KVPlaneIntersection.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      planeIntersections,
      setPlaneIntersections,
      rolodexPatch,
      kestrelModel,
    ]
  )

  const updateSurfaceArea = useMemo(
    () =>
      (
        surfaceArea: KVSurfaceArea,
        jsonFields: string | string[],
        setter: (a: KVSurfaceArea) => KVSurfaceArea,
        v: unknown | unknown[]
      ) => {
        if (!sendKVCommand) return
        updateMarkup({
          markup: surfaceArea,
          jsonFields,
          setter,
          v,
          commandType: 'updateSurfaceArea',
          markupArray: surfaceAreas,
          setMarkupArray: setSurfaceAreas,
          rolodexField: 'surfaceAreas',
          toJSON: KVSurfaceArea.toJSON,
          sendCommand: sendKVCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendKVCommand,
      surfaceAreas,
      setSurfaceAreas,
      rolodexPatch,
      kestrelModel,
    ]
  )

  return (
    <KestrelContextProvider
      {...{
        kestrelModel,
        setKestrelModel,
        authorizedAxios,
        kestrelStatus,
        setKestrelStatus,
        selectedMarkup,
        setSelectedMarkup,
        sendKVCommand,
        setSendKVCommand,
        popupMenuStartPosition,
        setPopupMenuStartPosition,
        pixelStreamingURL,
        setPixelStreamingURL,
        annotations,
        setAnnotations,
        updateAnnotation,
        polygons,
        setPolygons,
        updatePolygon,
        measurements,
        setMeasurements,
        updateMeasurement,
        planeIntersections,
        setPlaneIntersections,
        updatePlaneIntersection,
        surfaceAreas,
        setSurfaceAreas,
        updateSurfaceArea,
        cameraViews,
        setCameraViews,
        cameraPoses,
        setCameraPoses,
      }}>
      {children}
    </KestrelContextProvider>
  )
}

/**
 * A hook for accessing data from the Kestrel context
 */
export const useKestrel = (): KestrelContextType => {
  const context = useContext(KestrelContext)
  if (!context)
    throw new Error('useKestrel() must be used within a Kestrel context')
  return context
}
